import React, { useCallback } from 'react';
import { useActiveSubstanceFilters } from '@client/feature/activeSubstance/hooks/useActiveSubstanceFilters';
import { useActiveSubstances } from '@client/feature/activeSubstance/hooks/useActiveSubstance';
import Result from '@client/feature/activeSubstance/components/Search/Result/Result';
import { FilterPanels, BackgroundSection } from './partials';
import { useTranslation } from 'react-i18next';

/**
 * Main search page component for active substances with filters and results
 * @returns {JSX.Element} The active substance search page
 */
const ActiveSubstanceSearchPage = () => {
  const {
    filters,
    updateFilterWithLabels,
    updatePagination,
    updateSorting,
    getCurrentSorting,
    resetFilters,
    hasActiveFilters,
    getFilterLabel,
    shareToClipboard,
  } = useActiveSubstanceFilters();

  const { data, isLoading, error } = useActiveSubstances(filters);
  const { t } = useTranslation();
  const handlePageChange = useCallback(
    ({ page, size }) => {
      updatePagination({ page, size });
    },
    [updatePagination]
  );
  const handleSorting = useCallback(
    (field, order) => updateSorting(field, order),
    [updateSorting]
  );
  const handleReset = useCallback(() => resetFilters(), [resetFilters]);
  const handleShare = useCallback(() => shareToClipboard(), [shareToClipboard]);
  const handleRemoveFilter = useCallback(
    (filterKey, valueId) => {
      const currentValues = filters[filterKey] || [];
      const newValues = currentValues.filter((v) => v !== valueId);
      updateFilterWithLabels(filterKey, newValues);
    },
    [filters, updateFilterWithLabels]
  );

  return (
    <div className="relative w-full min-h-screen">
      <div className="container">
        <BackgroundSection />
        <div className="flex flex-col gap-8 relative z-10">
          <div className="filters-container">
            <FilterPanels
              filters={filters}
              onFilterChange={updateFilterWithLabels}
            />
          </div>

          <Result
            filters={filters}
            data={data}
            isLoading={isLoading}
            error={error}
            getFilterLabel={getFilterLabel}
            onResetFilters={handleReset}
            onRemoveFilter={handleRemoveFilter}
            onPageChange={handlePageChange}
            onSortChange={handleSorting}
            currentSorting={getCurrentSorting()}
            onShare={handleShare}
          />
        </div>
        {!isLoading && (
          <>
            <p>{t('active-substance|table.bottomText1')}</p>
            <p className="mb-4">{t('active-substance|table.bottomText2')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ActiveSubstanceSearchPage;
